// frontend/src/components/leads/leadsStateStyles.js
//
// The two tables the leads-area state surface is built from, kept apart from
// the component that renders them so a later `EmptyState` re-skin can import
// the same values instead of re-deriving them.
//
//   KIND_THEME    - what a STATE looks like: accent tone, glyph, whether the
//                   receipt row applies, whether it is allowed to celebrate.
//   VARIANT_STYLES - what a TREATMENT looks like: shell chrome, type scale,
//                   CTA skin. One entry since the owner picked "Ledger"
//                   (2026-09-19); Signal and Quiet were deleted with their
//                   renderers in LeadsStateCard.js.
//   AURORA        - the SECOND treatment, worn by the three "cleared" kinds
//                   only (AURORA_KINDS). See the block at the bottom.
//
// STATE DIFFERENCES NEVER FORK THE LAYOUT *WITHIN* A TREATMENT. A kind may
// change an accent, a glyph, which figures exist and which CTAs are offered -
// it may not change the arrangement. What DOES fork is which of the two
// treatments a kind wears, and that is a closed, named list of three.
//
// Direction is INHERITED (the page wrapper sets `dir`, AuthLayout.js:42), so
// every rule here is written with logical properties - `paddingInline`,
// `marginInlineStart`, `textAlign: start`. Nothing sets `dir`.

import {
  AlertTriangle,
  Archive,
  CircleCheck,
  Flame,
  Lock,
  MessageSquareReply,
  Radar,
  SearchX,
  Sigma,
  Tag,
  TrendingUp,
  WifiOff,
} from "lucide-react";

import { colors, typography, transitions } from "../../theme/tokens";
import { ACCENT_HEX } from "../InlineBanner";
import { HOT, REPUTATION, GREEN } from "../../theme/leadPalette";

// ---------------------------------------------------------------- tones

/**
 * Accent per semantic tone. `success` / `info` / `attention` / `error` are the
 * InlineBanner vocabulary, shared so a banner and a state card painted for the
 * same reason are painted the same colour. The three bucket tones come from the
 * KPI cards, so "hot" reads teal and "reputation" reads rose on the state card
 * exactly as it does on the metric above it.
 */
export const TONE_ACCENT = {
  success: ACCENT_HEX.success,
  info: ACCENT_HEX.info,
  attention: ACCENT_HEX.attention,
  error: ACCENT_HEX.error,
  hot: HOT.stops[1], // #00A896 - the deeper of the hot pair, readable as ink
  reputation: REPUTATION.text, // #C56C7A
  contacted: GREEN.text, // #059669
};

/**
 * The FILLED-BUTTON ink per tone, and why it is a second table rather than the
 * accent itself.
 *
 * The accents above are chrome colours - a glyph, a wash, a hairline, a rail.
 * Several of them are pastels, and white 14px text on a pastel is illegible:
 * measured against white, `attention` (#E88A73) is 2.5:1, `reputation` 3.6,
 * `contacted` 3.8, `hot` 3.7, `info` 3.6 - all below the 4.5 WCAG AA floor for
 * normal text, and `success` (#40E0D0) is far worse. Every value below is a
 * darkened relative of its accent, so the state still reads in its own colour,
 * and every one clears 4.8:1 on white (checked, not guessed).
 *
 * The alternative - one brand-ink button everywhere - was rejected because the
 * CTA is the loudest element on the card and losing its state colour flattens
 * an error, a lapse and a celebration into the same object.
 */
export const TONE_CTA_INK = {
  success: "#00796B", // 5.32:1
  info: "#38708A", // 5.45:1
  attention: "#A34C34", // 5.77:1
  error: "#8F4747", // 6.65:1
  hot: "#0F766E", // 5.47:1
  reputation: "#A4485A", // 5.75:1
  contacted: "#047857", // 5.48:1
};

/** 12% tint suffix - the soft badge/chip fill synthesised from a raw accent. */
export const TINT_ALPHA = "1F";

// ---------------------------------------------------------------- kinds

/**
 * Every state the leads area can be in, and how it is painted.
 *
 * @property {string}  tone       key into TONE_ACCENT.
 * @property {Function} Icon      Lucide glyph. `hideGlyph` suppresses it.
 * @property {boolean} hideGlyph  the waiting state deliberately has no badge -
 *   the owner removed it on 2026-08-19 ("childy and not elegant") and this
 *   table must not quietly reinstate it in a new treatment.
 * @property {boolean} celebrate  arms the Ledger treatment's ring draw. TRUE on
 *   the "you cleared it" states only; never on an error, and never on a lapsed
 *   account, which has nothing to be congratulated for. It describes the
 *   STATE, not the renderer: since 2026-09-20 those three kinds wear the
 *   Aurora treatment, which carries no glyph badge at all and celebrates
 *   through the surface itself - so nothing reads the flag today. It stays
 *   TRUE because the character of the state has not changed and a kind routed
 *   back to Ledger must celebrate again without a second edit here.
 * @property {boolean} receipt    does the receipt row apply. Read by
 *   `getStateContent`, and honoured by both treatments.
 *
 * There is deliberately no `hero` property. It existed while the three cleared
 * kinds carried a hero figure, was never actually read (getStateContent named
 * the figure per kind), and the owner removed the figure from those kinds on
 * 2026-09-20.
 */
export const KIND_THEME = {
  "all-handled": {
    tone: "success",
    Icon: CircleCheck,
    celebrate: true,
    receipt: true,
  },
  "bucket-cleared-hot": {
    tone: "hot",
    Icon: CircleCheck,
    celebrate: true,
    receipt: false,
  },
  "bucket-cleared-authority": {
    tone: "reputation",
    Icon: CircleCheck,
    celebrate: true,
    receipt: false,
  },
  "bucket-empty-hot": {
    tone: "hot",
    Icon: Flame,
    celebrate: false,
    receipt: false,
  },
  "bucket-empty-authority": {
    tone: "reputation",
    Icon: TrendingUp,
    celebrate: false,
    receipt: false,
  },
  "contacted-empty": {
    tone: "contacted",
    // NOT a bell: that is the notifications glyph everywhere else in the
    // product, and this state is about marking that you reached out.
    Icon: MessageSquareReply,
    celebrate: false,
    receipt: false,
  },
  "filtered-empty": {
    tone: "info",
    Icon: SearchX,
    celebrate: false,
    receipt: false,
  },
  "stats-unavailable": {
    tone: "info",
    Icon: Sigma,
    celebrate: false,
    receipt: false,
  },
  error: {
    tone: "error",
    Icon: AlertTriangle,
    celebrate: false,
    receipt: false,
  },
  offline: {
    tone: "attention",
    Icon: WifiOff,
    celebrate: false,
    receipt: false,
  },
  lapsed: {
    tone: "attention",
    Icon: Lock,
    celebrate: false,
    receipt: false,
  },
  "awaiting-first": {
    tone: "success",
    Icon: Radar,
    hideGlyph: true,
    celebrate: false,
    receipt: false,
    // The ONLY kind that may carry the animated scanning band. Declared here
    // rather than checked as `kind === "awaiting-first"` in the renderer so
    // the question "which states animate" has one answer, in the table that
    // already answers every other per-state look. A scene on a lapsed account
    // or an outage would be a live indicator over a dead system.
    scene: true,
  },
  // The archive view with nothing in it. Neutral on purpose: an empty archive
  // is neither an achievement nor a problem, so no celebration and no receipt.
  "archive-empty": {
    tone: "info",
    Icon: Archive,
    celebrate: false,
    receipt: false,
  },
  // A label view with nothing in it - the same neutral card as the empty
  // archive: nothing to celebrate, nothing wrong, the chip row above is the
  // way back.
  "label-empty": {
    tone: "info",
    Icon: Tag,
    celebrate: false,
    receipt: false,
  },
};

/**
 * @param {string} kind
 * @returns {string} the kind's accent hex, falling back to the neutral info
 *   tone rather than throwing - an unknown kind must degrade to a plain card,
 *   never blank the dashboard.
 */
export const accentFor = (kind) =>
  TONE_ACCENT[KIND_THEME[kind]?.tone] ?? TONE_ACCENT.info;

/**
 * @param {string} kind
 * @returns {string} the kind's AA-safe filled-button ink (see TONE_CTA_INK).
 */
export const ctaInkFor = (kind) =>
  TONE_CTA_INK[KIND_THEME[kind]?.tone] ?? TONE_CTA_INK.info;

// ---------------------------------------------------------------- shared geometry

/**
 * The minimum TAP AREA for any control in this family, in px.
 *
 * It is a floor on the BOX, never on the glyph: an icon button may keep a 16px
 * X and still be 44 wide, and the two are different decisions. Declared here
 * rather than typed at each site because the controls that need it live in
 * three files (the state card's CTAs, the first-steps dismiss, the welcome
 * dialog's close) and a floor that is re-typed is a floor that drifts - both
 * icon buttons were measured under it at 360px before this constant existed.
 */
export const TAP_TARGET_MIN = 44;

/*
 * There WAS a `visuallyHiddenSx` here. Its only consumer was the hero figure's
 * spoken sibling - the settled number that a screen reader read while the
 * digits counted up beside it - and the hero went with the Aurora round on
 * 2026-09-20. The recipe itself still lives in components/shared/
 * AuthorNameText.js, which is where this copy came from; a new caller should
 * take it from there rather than restore an export nothing imports.
 */

/**
 * CTA geometry shared by all three treatments, lifted verbatim from
 * EmptyState.js so a state card and an empty state never show two different
 * button sizes on one screen. 46px at xs clears TAP_TARGET_MIN.
 */
export const ctaBaseSx = {
  height: { xs: 46, sm: 42 },
  px: { xs: 2.25, sm: 3 },
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 600,
  fontFamily: typography.fontFamily.body,
  letterSpacing: "0.005em",
  textTransform: "none",
  width: "auto",
  gap: 1.25,
  flexShrink: 0,
};

/**
 * Filled CTA in an arbitrary accent. Deliberately a flat accent fill rather
 * than the brand gradient: the state card's accent is the STATE's colour, and a
 * turquoise gradient on an error card would say the wrong thing.
 *
 * @param {string} accent the AA-safe ink from `ctaInkFor`, NOT the chrome
 *   accent - see TONE_CTA_INK for why those are two different values.
 * @returns {object} sx
 */
export const solidCtaSx = (accent) => ({
  ...ctaBaseSx,
  color: "#ffffff",
  // `background`, NOT `backgroundColor`. The theme paints every contained
  // button with `background: linear-gradient(...)` (theme.js MuiButton.contained),
  // and a gradient is a background-IMAGE - it sits on top of any
  // background-color an sx block sets, at any specificity. Every state accent
  // was therefore being repainted brand turquoise, so an error card offered a
  // cheerful teal "try again". Setting the shorthand replaces the image.
  background: accent,
  boxShadow: `0 4px 14px ${accent}3D`,
  transition: `box-shadow ${transitions.base}, transform ${transitions.base}, filter ${transitions.base}`,
  "&:hover": {
    background: accent,
    filter: "brightness(1.06)",
    boxShadow: `0 6px 20px ${accent}52`,
    transform: "translateY(-1px)",
  },
  // Android applies :hover on tap and it sticks - pin the tap-time state.
  "@media (hover: none)": {
    "&:hover": {
      background: accent,
      filter: "none",
      boxShadow: `0 4px 14px ${accent}3D`,
      transform: "none",
    },
  },
  "&:active": { transform: "translateY(0)" },
  "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
});

/**
 * Outlined sibling. Always drawn in the AA-safe brand ink, never in the state
 * accent: a muted brick or dusty salmon outline at 14px fails contrast.
 *
 * @returns {object} sx
 */
export const quietCtaSx = () => ({
  ...ctaBaseSx,
  color: colors.primary.accessible,
  borderColor: `${colors.primary.accessible}AA`,
  "&:hover": {
    borderColor: colors.primary.accessible,
    backgroundColor: colors.primary.alpha08,
  },
  "@media (hover: none)": {
    "&:hover": {
      borderColor: `${colors.primary.accessible}AA`,
      backgroundColor: "transparent",
    },
  },
  "&:focus-visible": {
    outline: `2px solid ${colors.primary.accessible}`,
    outlineOffset: 2,
  },
});

// ---------------------------------------------------------------- treatments

/**
 * Option 1 - "Ledger".
 *
 * The ceramic family evolved: EmptyState's own chrome (24px radius, layered
 * shadow with the inset top highlight, a diagonal accent wash, a masked dot
 * grid). Picking this option costs no follow-up: E's three states already wear
 * this chrome, so the whole deck reads as one object on day one.
 *
 * It carries no hero type scale any more. The hero figure only ever appeared
 * on the three "cleared" kinds, and those wear AURORA below since 2026-09-20.
 */
const LEDGER = {
  id: "ledger",
  name: "Ledger",
  align: "center",
  shell: (accent) => ({
    position: "relative",
    overflow: "hidden",
    textAlign: "center",
    background: {
      xs: `linear-gradient(135deg, ${accent}3A 0%, ${accent}12 22%, ${colors.background.default} 50%, ${colors.primary.alpha08} 78%, ${colors.primary.alpha18} 100%)`,
      sm: `linear-gradient(135deg, ${accent}47 0%, ${accent}1A 26%, ${colors.background.default} 50%, ${colors.primary.alpha12} 74%, ${colors.primary.alpha22} 100%)`,
    },
    border: `1px solid ${accent}1F`,
    borderRadius: "24px",
    boxShadow: `
      0 8px 32px rgba(20, 30, 45, 0.06),
      0 2px 6px rgba(20, 30, 45, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.9)
    `,
    paddingInline: { xs: 3, sm: 6 },
    py: { xs: 4, sm: 5.5 },
  }),
  title: {
    fontFamily: typography.fontFamily.heading,
    fontSize: { xs: "1.2rem", sm: "1.4rem" },
    fontWeight: 700,
    color: colors.text.heading,
    letterSpacing: "-0.015em",
    lineHeight: 1.3,
    m: 0,
  },
  body: {
    fontFamily: typography.fontFamily.body,
    fontSize: { xs: "13.5px", sm: "14.5px" },
    color: colors.text.label,
    lineHeight: 1.65,
    maxWidth: 440,
    mx: "auto",
  },
};

/**
 * The treatments, keyed by their `variant` prop value.
 *
 * ONE entry since the owner picked Ledger on 2026-09-19. It stays a table
 * rather than becoming a bare constant because every call site already passes
 * `variant`, and `variantStyles` below is what makes an unknown value render a
 * real card instead of crashing the leads area.
 */
export const VARIANT_STYLES = {
  ledger: LEDGER,
};

/** Every variant id. */
export const VARIANT_IDS = ["ledger"];

/**
 * @param {string} variant
 * @returns {object} the treatment, defaulting to Ledger - an unknown variant
 *   must render a real card, not crash the leads area.
 */
export const variantStyles = (variant) =>
  VARIANT_STYLES[variant] ?? VARIANT_STYLES.ledger;

// ---------------------------------------------------------------- aurora

/**
 * The three kinds that wear the AURORA treatment instead of Ledger.
 *
 * A CLOSED LIST, and the owner knows the leads area now carries two visual
 * languages (2026-09-20). It is these three because they are the only states
 * that say "you are done" - the moment the card is allowed to be a pleasure
 * rather than a notice. Every other kind (an outage, a lapse, an empty filter)
 * keeps the Ledger chrome, which is the right register for them.
 *
 * Order is the resolver's, so a reader can line the two files up.
 */
export const AURORA_KINDS = Object.freeze([
  "all-handled",
  "bucket-cleared-hot",
  "bucket-cleared-authority",
]);

const AURORA_KIND_SET = new Set(AURORA_KINDS);

/**
 * @param {string} kind
 * @returns {boolean} does this kind wear the Aurora treatment.
 */
export const isAuroraKind = (kind) => AURORA_KIND_SET.has(kind);

/**
 * The per-kind colour tokens the Aurora stylesheet reads, written onto the
 * card root as inline custom properties.
 *
 * WHY CUSTOM PROPERTIES RATHER THAN sx VALUES: every one of them is consumed
 * inside `styles/leadsArea.css` - by a @keyframes-driven layer, by a mask, or
 * by an SVG `fill` several elements deep. None of those can be reached from an
 * `sx` block on the root, and moving the layers into `sx` would put keyframes
 * back inline, which this family bans (see the header of leadsArea.css).
 *
 * EVERY TOKEN HERE IS READ BY THE STYLESHEET, and a test enforces it both
 * ways. The first cut carried two that were not - `--lfs-au-beam-rgb` and
 * `--lfs-au-ac2-rgb` - under a doc block confidently describing what they did.
 * A documented token with no consumer is worse than no token: it is a claim a
 * reader has no reason to doubt.
 *
 *   --lfs-au-ac-rgb   the kind's accent, as an "r, g, b" triple so the
 *                     stylesheet can pick its own alpha per layer. It paints
 *                     the first and third ribbons, the sheets' tint and THE
 *                     BEAM - the beam has no colour of its own.
 *   --lfs-au-dot-rgb  the ink the dot field, the beam's core and the entry
 *                     scan line are drawn in - it has to read on white, so it
 *                     is always the DEEP member of the kind's pair.
 *   --lfs-au-sky-rgb  every seventh dot, so the field is not monochrome.
 *   --lfs-au-r2       the SECOND ribbon's whole colour. A whole colour rather
 *                     than an alpha over a counterpoint token, because on
 *                     all-handled the owner approved a faint warm pink here
 *                     (2026-09-20) that is not a relative of anything else on
 *                     the card - one token says that honestly, two did not.
 *   --lfs-au-rail-a/b the 3px top rail's PHYSICAL pair - `a` is the bright end
 *                     and sits on the left at every `dir`, `b` is the dark end
 *                     on the right. See the gradient rule in leadsArea.css.
 */
export const AURORA_VARS = Object.freeze({
  "all-handled": {
    "--lfs-au-ac-rgb": "64, 224, 208",
    "--lfs-au-dot-rgb": "0, 168, 150",
    "--lfs-au-sky-rgb": "44, 120, 168",
    "--lfs-au-r2": "rgba(236, 170, 184, 0.17)",
    "--lfs-au-rail-a": "#40E0D0",
    "--lfs-au-rail-b": "#00796B",
  },
  "bucket-cleared-hot": {
    "--lfs-au-ac-rgb": "0, 168, 150",
    "--lfs-au-dot-rgb": "0, 168, 150",
    "--lfs-au-sky-rgb": "44, 120, 168",
    "--lfs-au-r2": "rgba(64, 224, 208, 0.15)",
    "--lfs-au-rail-a": "#40E0D0",
    "--lfs-au-rail-b": "#0F766E",
  },
  "bucket-cleared-authority": {
    "--lfs-au-ac-rgb": "197, 108, 122",
    "--lfs-au-dot-rgb": "197, 108, 122",
    "--lfs-au-sky-rgb": "164, 72, 90",
    "--lfs-au-r2": "rgba(239, 161, 173, 0.15)",
    "--lfs-au-rail-a": "#EFA1AD",
    "--lfs-au-rail-b": "#A4485A",
  },
});

/**
 * @param {string} kind
 * @returns {object} the kind's Aurora custom properties, falling back to the
 *   all-handled set rather than returning `{}` - an empty set would leave
 *   `rgba(var(--lfs-au-ac-rgb), .26)` invalid and paint the ribbons black.
 */
export const auroraVarsFor = (kind) => AURORA_VARS[kind] ?? AURORA_VARS["all-handled"];

/**
 * The Aurora ink ramp. Written as literals rather than taken from
 * `theme/tokens` on purpose: this is a designed surface of its own with a
 * warmer paper than the app's `background.default`, and its text was chosen
 * and contrast-measured AGAINST that paper. Borrowing the app greys would be a
 * different, unmeasured pairing.
 *
 * Measured on the rendered paper (#F6F6F3): ink 13.9:1, ink2 8.0:1,
 * ink3 6.5:1 - all clear of the 4.5:1 AA floor for normal text.
 */
export const AURORA_INK = Object.freeze({
  ink: "#1B2430",
  ink2: "#3F4B58",
  ink3: "#4A5563",
  hairline: "rgba(27, 36, 48, 0.12)",
});

/**
 * Option 2 - "Aurora, bright" (owner-approved 2026-09-20, ported from the
 * prototype of the same name).
 *
 * Depth on paper cannot come from saturation, so it comes from LAYERS: drifting
 * accent ribbons far back, translucent sheets with their own cast shadows
 * floating above the paper, one broad highlight crossing the surface, and three
 * parallax layers of a seeded dot field that INK UP as a soft beam crosses
 * them. All of that lives in `styles/leadsArea.css` under `.lfs-aurora`; what
 * is here is only the shell and the type.
 *
 * The API matches a VARIANT_STYLES entry (`shell(accent)`, `title`, `body`) so
 * the card can pick a treatment and render one code path.
 */
export const AURORA = {
  id: "aurora",
  name: "Aurora, bright",
  align: "center",
  /**
   * @param {string} [_accent] ignored. Aurora takes its colour from the
   *   per-kind custom properties, not from a single accent value; the
   *   parameter exists so the two treatments share one call shape.
   */
  shell: (_accent) => ({
    position: "relative",
    overflow: "hidden",
    textAlign: "center",
    // A warm off-white that falls very slightly cool toward the bottom, so the
    // paper has a direction before a single layer is drawn on it.
    background: "linear-gradient(170deg, #FBFAF7 0%, #F6F6F3 50%, #F0F3F2 100%)",
    // Six shadows: a contact shadow, a mid lift, a wide ambient one, the inset
    // top highlight that makes the edge read as a lit lip, an inset vignette
    // that curves the paper inward, and a hairline ring in place of a border
    // (a real border would clip the field's `inset: 0`).
    boxShadow: [
      "0 1px 2px rgba(27, 36, 48, 0.05)",
      "0 10px 22px -12px rgba(27, 36, 48, 0.14)",
      "0 44px 80px -44px rgba(27, 36, 48, 0.30)",
      "inset 0 1px 0 #fff",
      "inset 0 0 90px rgba(27, 36, 48, 0.055)",
      "inset 0 -1px 0 rgba(27, 36, 48, 0.04)",
      "0 0 0 1px rgba(27, 36, 48, 0.06)",
    ].join(", "),
    borderRadius: { xs: "22px", sm: "26px" },
    // THE CLAMP (owner, 2026-09-20). He approved this design in a prototype
    // that was capped at 960px; production stretches the leads area to
    // 1318-1488px, where the composition comes apart - the beam has half a
    // card of nothing to cross and the copy column is an island. 1040 keeps
    // the proportion he actually reviewed without reading as artificially
    // narrow. It is on the AURORA shell alone: the ten Ledger kinds keep
    // today's full-width behaviour, and nothing about the dashboard container
    // changes. Phone and tablet are already under it, so `mx: auto` is inert
    // there and not a pixel moves.
    maxWidth: 1040,
    mx: "auto",
    // 18px of side padding at xs is what lets the three receipt cells stay one
    // row at 320. Measured, not guessed - see the receipt note in
    // LeadsStateCard.js.
    paddingInline: { xs: "18px", sm: "48px" },
    // Asymmetric, and the top is the larger: with the hero figure gone the
    // title leads, and it needs air above it or the card reads as cropped.
    pt: { xs: "44px", sm: "58px" },
    pb: { xs: "30px", sm: "44px" },
  }),
  title: {
    fontFamily: typography.fontFamily.heading,
    fontSize: { xs: "22px", sm: "26px" },
    fontWeight: 700,
    color: AURORA_INK.ink,
    letterSpacing: "-0.015em",
    lineHeight: 1.3,
    m: 0,
  },
  body: {
    fontFamily: typography.fontFamily.body,
    fontSize: { xs: "13.5px", sm: "14.5px" },
    color: AURORA_INK.ink2,
    lineHeight: 1.7,
    maxWidth: 620,
    mx: "auto",
  },
  meta: {
    fontFamily: typography.fontFamily.body,
    fontSize: "12px",
    color: AURORA_INK.ink3,
  },
  receiptValue: {
    fontFamily: typography.fontFamily.heading,
    fontWeight: 700,
    fontSize: { xs: "16px", sm: "18px" },
    lineHeight: 1.15,
    color: AURORA_INK.ink,
    fontVariantNumeric: "tabular-nums",
  },
  receiptLabel: {
    fontFamily: typography.fontFamily.body,
    fontSize: { xs: "11px", sm: "11.5px" },
    letterSpacing: { xs: 0, sm: "0.015em" },
    color: AURORA_INK.ink3,
    // One row at every width: the three labels never wrap and never truncate.
    whiteSpace: "nowrap",
    mt: "2px",
  },
  /**
   * The CTA, allowed to fit.
   *
   * `ctaBaseSx` is `width: auto` + `flexShrink: 0` + a FIXED height, which is
   * right for the short labels it was written for and wrong here: at a 320
   * viewport the Aurora card's content box is 252px and the English
   * "Go to reputation opportunities" measures 271.6px, so the button broke out
   * of the card by ~10px each side and the card's own `overflow: hidden`
   * swallowed the evidence - no page scrollbar, nothing for a test to catch.
   *
   * The label is approved copy, so truncating it is not an option and neither
   * is shrinking the type. It wraps instead: `minHeight` in place of `height`
   * so a second line grows the button rather than being clipped, `maxWidth`
   * and a live `flexShrink` so it can never exceed the content box, and the
   * SAME minimum heights as the base, so a label that fits on one line renders
   * pixel-identically to before.
   */
  cta: {
    flexShrink: 1,
    maxWidth: "100%",
    height: "auto",
    minHeight: { xs: 46, sm: 42 },
    whiteSpace: "normal",
    py: "6px",
    lineHeight: 1.3,
  },
};

// ---------------------------------------------------------------- scenes

/**
 * The animated treatment of the `awaiting-first` band, as the wire value of
 * `LeadsStateCard`'s `scene` prop.
 *
 * The owner picked "Sweep" on 2026-09-19; the CSS Spotlight and the pseudo-3D
 * Depth field were deleted with their engines. What is still open is the beam's
 * STRENGTH of the beam - and that lives with the rest of the scene's
 * numbers in `scanning/scanningConstants.js`, not here: it is a parameter of one
 * treatment, not a second treatment.
 */
export const CHOSEN_SCENE = "sweep";
